import { useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { formatDate, formatNumber } from '../i18n.js';
import type { Severity } from './ui.js';

/**
 * The graphics of a site card on the start page (T-55a). Each chart answers hover, touch and the
 * arrow keys: the day under the pointer (or the focused one) is read out in a line below, so the
 * numbers never live in colour or a tooltip alone.
 */

const scoreTone = (score: number) =>
  score >= 90 ? 'text-green-700' : score >= 65 ? 'text-amber-600' : 'text-red-700';

/** Ring with the Health Score; the grade in the middle. */
export function ScoreRing({ score, grade }: { score: number | null; grade: string | null }) {
  const { t } = useTranslation();
  const r = 26;
  const length = 2 * Math.PI * r;
  const filled = score === null ? 0 : (Math.max(0, Math.min(100, score)) / 100) * length;
  return (
    <svg
      viewBox="0 0 64 64"
      className={`h-16 w-16 shrink-0 ${score === null ? 'text-slate-400' : scoreTone(score)}`}
      role="img"
      aria-label={
        score === null || !grade
          ? t('overview.card.noScore')
          : t('overview.card.scoreSr', { score, grade })
      }
    >
      <circle
        cx="32"
        cy="32"
        r={r}
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.15"
        strokeWidth="7"
      />
      <circle
        cx="32"
        cy="32"
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth="7"
        strokeLinecap="round"
        strokeDasharray={`${filled} ${length}`}
        transform="rotate(-90 32 32)"
      />
      <text x="32" y="30" textAnchor="middle" className="fill-slate-900 text-[18px] font-bold">
        {grade ?? '–'}
      </text>
      <text x="32" y="45" textAnchor="middle" className="fill-slate-700 text-[11px]">
        {score ?? ''}
      </text>
    </svg>
  );
}

/** Arrow keys move the highlighted point; Home/End jump to the ends. */
function useActivePoint(count: number) {
  const [active, setActive] = useState<number | null>(null);
  const onKeyDown = (e: KeyboardEvent) => {
    if (count === 0) return;
    const current = active ?? count - 1;
    const next =
      e.key === 'ArrowLeft'
        ? Math.max(0, current - 1)
        : e.key === 'ArrowRight'
          ? Math.min(count - 1, current + 1)
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? count - 1
              : null;
    if (next === null) return;
    e.preventDefault();
    setActive(next);
  };
  return { active, setActive, onKeyDown };
}

function ChartFrame({
  label,
  readout,
  readoutId,
  children,
}: {
  label: string;
  readout: ReactNode;
  readoutId: string;
  children: ReactNode;
}) {
  return (
    <figure className="min-w-0">
      <figcaption className="mb-1 text-sm font-medium text-slate-800">{label}</figcaption>
      {children}
      <p id={readoutId} aria-live="polite" className="mt-1 min-h-5 text-sm text-slate-700">
        {readout}
      </p>
    </figure>
  );
}

/** Health Score of the last 30 days as a line with points to explore. */
export function HealthTrend({ points }: { points: readonly { day: string; score: number }[] }) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const { active, setActive, onKeyDown } = useActivePoint(points.length);
  if (points.length < 2)
    return (
      <ChartFrame
        label={t('overview.card.trend')}
        readout={t('overview.card.trendEmpty')}
        readoutId={id}
      >
        <div className="h-12 rounded bg-slate-50" aria-hidden="true" />
      </ChartFrame>
    );
  const w = 240;
  const h = 48;
  const min = Math.min(...points.map((p) => p.score), 50);
  const x = (i: number) => (i / (points.length - 1)) * (w - 8) + 4;
  const y = (v: number) => h - 4 - ((v - min) / (100 - min || 1)) * (h - 8);
  const shown = active ?? points.length - 1;
  const point = points[shown];
  const first = points[0]?.score ?? 0;
  const last = points[points.length - 1]?.score ?? 0;
  return (
    <ChartFrame
      label={t('overview.card.trend')}
      readoutId={id}
      readout={
        point
          ? t('overview.card.trendPoint', {
              date: formatDate(point.day, i18n.language),
              score: point.score,
            })
          : null
      }
    >
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className={`h-12 w-full cursor-crosshair rounded focus-visible:outline-2 focus-visible:outline-blue-700 ${scoreTone(last)}`}
        preserveAspectRatio="none"
        role="img"
        tabIndex={0}
        aria-label={t('overview.card.trendSr', { from: first, to: last, days: points.length })}
        aria-describedby={id}
        onKeyDown={onKeyDown}
        onPointerLeave={() => setActive(null)}
      >
        <polyline
          points={points.map((p, i) => `${x(i)},${y(p.score)}`).join(' ')}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        {point ? <circle cx={x(shown)} cy={y(point.score)} r="3.5" fill="currentColor" /> : null}
        {points.map((p, i) => (
          <rect
            key={p.day}
            x={x(i) - w / points.length / 2}
            y="0"
            width={w / points.length}
            height={h}
            fill="transparent"
            onPointerEnter={() => setActive(i)}
          />
        ))}
      </svg>
    </ChartFrame>
  );
}

export interface UptimeDay {
  readonly day: string;
  readonly pct: number | null;
  readonly avgResponseMs: number | null;
}

const uptimeTone = (pct: number | null) =>
  pct === null
    ? 'bg-slate-200'
    : pct >= 99.9
      ? 'bg-green-600'
      : pct >= 99
        ? 'bg-amber-400'
        : 'bg-red-600';

/** The last 30 days as a strip of day bars; days without data stay grey. */
export function UptimeStrip({ days, today }: { days: readonly UptimeDay[]; today: Date }) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const byDay = new Map(days.map((d) => [d.day, d]));
  const all: UptimeDay[] = Array.from({ length: 30 }, (_, i) => {
    const day = new Date(today.getTime() - (29 - i) * 86_400_000).toISOString().slice(0, 10);
    return byDay.get(day) ?? { day, pct: null, avgResponseMs: null };
  });
  const { active, setActive, onKeyDown } = useActivePoint(all.length);
  const shown = all[active ?? all.length - 1];
  const measured = all.filter((d) => d.pct !== null).length;
  return (
    <ChartFrame
      label={t('overview.card.uptime')}
      readoutId={id}
      readout={
        shown
          ? shown.pct === null
            ? t('overview.card.uptimeNone', { date: formatDate(shown.day, i18n.language) })
            : t('overview.card.uptimeDay', {
                date: formatDate(shown.day, i18n.language),
                pct: formatNumber(shown.pct, i18n.language, 2),
                ms: shown.avgResponseMs ?? '–',
              })
          : null
      }
    >
      <div
        role="img"
        tabIndex={0}
        aria-label={t('overview.card.uptimeSr', { days: measured })}
        aria-describedby={id}
        onKeyDown={onKeyDown}
        onPointerLeave={() => setActive(null)}
        className="flex h-8 items-stretch gap-px rounded focus-visible:outline-2 focus-visible:outline-blue-700"
      >
        {all.map((d, i) => (
          <span
            key={d.day}
            onPointerEnter={() => setActive(i)}
            className={`flex-1 rounded-sm ${uptimeTone(d.pct)} ${i === active ? 'ring-2 ring-slate-900' : ''}`}
          />
        ))}
      </div>
    </ChartFrame>
  );
}

const SEVERITY_BAR: Readonly<Record<Severity, string>> = {
  critical: 'bg-red-700',
  high: 'bg-orange-500',
  medium: 'bg-amber-400',
  low: 'bg-slate-400',
  info: 'bg-blue-300',
};

/** Open problems as one stacked bar, with a link per severity to the filtered list. */
export function SeverityBar({
  siteId,
  counts,
  order,
}: {
  siteId: string;
  counts: Readonly<Record<Severity, number>>;
  order: readonly Severity[];
}) {
  const { t } = useTranslation();
  const total = order.reduce((n, s) => n + counts[s], 0);
  if (total === 0)
    return <p className="text-sm font-medium text-green-800">{t('overview.noProblems')}</p>;
  const present = order.filter((s) => counts[s] > 0);
  return (
    <div>
      <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
        {present.map((s) => (
          <span
            key={s}
            className={SEVERITY_BAR[s]}
            style={{ width: `${(counts[s] / total) * 100}%` }}
          />
        ))}
      </div>
      <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-sm">
        {present.map((s) => (
          <li key={s}>
            <Link
              to={`/sites/${siteId}/findings?severity=${s}`}
              className="inline-flex items-center gap-1.5 rounded text-slate-800 underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-blue-700"
            >
              <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-full ${SEVERITY_BAR[s]}`} />
              {t('overview.card.severityCount', { count: counts[s], severity: t(`severity.${s}`) })}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
